import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';

import { UserService, EdlinkFullSyncResult } from 'src/app/services/user.service';
import { NotificationToasterService } from 'src/app/services/notification-toaster.service';
import { ModalManagerService } from 'src/app/services/modal-manager.service';
import { CustomerSupportService } from 'src/app/services/customer-support.service';
import { EdlinkIntegration } from 'src/app/models/user';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import {
  GraphicModalCloseResultEnum,
  GraphicModalVariantEnum,
} from 'src/app/standalones/components/modals/graphic-modal/graphic-modal.component';

@UntilDestroy()
@Component({
  selector: 'app-user-settings-integrations-edlink',
  templateUrl: './user-settings-integrations-edlink.component.html',
  styleUrls: ['./user-settings-integrations-edlink.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsIntegrationsEdlinkComponent implements OnInit {
  protected readonly isSyncing$ = new BehaviorSubject<boolean>(false);
  protected readonly edlinkIntegration$ = new BehaviorSubject<EdlinkIntegration | null>(null);

  constructor(
    private readonly userService: UserService,
    private readonly notificationToaster: NotificationToasterService,
    private readonly translateService: TranslateService,
    private readonly modalManagerService: ModalManagerService,
    private readonly customerSupportService: CustomerSupportService,
  ) {}

  ngOnInit(): void {
    this.edlinkIntegration$.next(
      this.userService.user.getValue()?.user?.institution?.edlinkIntegration ?? null,
    );
  }

  protected syncEdlink(): void {
    if (this.isSyncing$.getValue()) {
      return;
    }

    this.isSyncing$.next(true);
    this.userService
      .syncEdlinkFull()
      .pipe(
        untilDestroyed(this),
        finalize(() => this.isSyncing$.next(false)),
      )
      .subscribe({
        next: (result: EdlinkFullSyncResult) => {
          // Optimistic, atomic update — syncedAt and syncedUserCount always land together,
          // so the button can't stay enabled while the count says otherwise.
          this.edlinkIntegration$.next({
            syncedAt: new Date().toISOString(),
            syncedUserCount: result.succeededCount,
            syncedSiteCount: result.syncedSiteCount,
          });
          this.userService.refreshUser();
          this.showSyncResultNotification(result);
        },
        error: () => {
          this.notificationToaster.showDefaultErrorNotificationV2(
            this.translateService.instant('Sync failed'),
            '',
            this.notificationToaster.generateNotificationCode(),
            undefined,
            undefined,
            undefined,
            true,
          );
        },
      });
  }

  // Edlink setup (the application secret + site mappings) is provisioned by Pencil support today,
  // not self-service — this prompts the admin to reach out rather than attempting a sync that
  // would fail server-side with no EdlinkIntegration row configured.
  protected openContactSupportPrompt(): void {
    this.modalManagerService
      .showGraphicModal({
        title: this.translateService.instant(
          'Contact our support team to set up your Edlink account',
        ),
        description: this.translateService.instant(
          "We'll help you seamlessly connect each site to its LMS or SIS",
        ),
        imageVariant: GraphicModalVariantEnum.ICON,
        icon: {
          name: 'support-headset',
          type: IconTypes.svgIcon,
        },
        primaryButtonConfig: {
          label: this.translateService.instant('Contact Support'),
          icon: 'arrow_forward',
          iconType: IconTypes.materialIcon,
        },
      })
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        if (result === GraphicModalCloseResultEnum.PRIMARY) {
          this.customerSupportService.openSupport({
            showCommand: 'showNewMessage',
            target: this.translateService.instant(
              "Hi, I'd like to set up Edlink for my institution",
            ),
          });
        }
      });
  }

  private showSyncResultNotification(result: EdlinkFullSyncResult): void {
    if (result.failedCount > 0) {
      this.notificationToaster.showDefaultWarningNotificationV2(
        this.translateService.instant('Sync completed with errors'),
        this.translateService.instant(
          '{{failedCount}} user(s) failed to sync. Please contact support for assistance.',
          { failedCount: result.failedCount },
        ),
        this.notificationToaster.generateNotificationCode(),
        undefined,
        undefined,
        undefined,
        true,
      );
    } else {
      this.notificationToaster.showDefaultSuccessNotification(
        this.translateService.instant('Sync completed'),
        this.notificationToaster.generateNotificationCode(),
        true,
      );
    }
  }
}
